"use client";

/**
 * The FAQs tab of an add-on or shop item: FAQs picked from the venue's FAQ
 * bank (Settings -> FAQs), each with who may see it, as on a show's FAQs
 * tab: one tab per venue language, listing the FAQs written in it.
 * Removing one archives its link; the venue FAQ stays in the bank.
 */

import { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MultiSelectEnhanced } from "@/components/ui/multi-select-enhanced";
import ConfirmDialog from "@/components/admin/shared/confirm-dialog";
import { FaqVisibilitySelect } from "@/components/admin/faqs/faq-visibility-select";
import type { FaqVisibility } from "@/lib/constants/faq-visibility";
import { AVAILABLE_LANGUAGES } from "@/lib/constants/languages";
import {
  listItemFaqs,
  removeItemFaq,
  setItemFaqVisibility,
  setItemFaqs,
  type FaqItemKind,
  type ItemFaqLink,
} from "@/app/actions/item-faqs";

interface ItemFaqsTabProps {
  kind: FaqItemKind;
  itemId: number;
  itemName: string;
  venueId: number;
}

/** A venue FAQ as /api/venues/[venueId]/faqs gives it. */
interface VenueFaq {
  faq_id: number;
  questions: Record<string, string>;
  answers: Record<string, string>;
  status: { id: number; name: string } | null;
}

const TITLE: Record<FaqItemKind, string> = {
  add_on: "Add-on FAQs",
  shop_item: "Shop item FAQs",
};

const NOUN: Record<FaqItemKind, string> = {
  add_on: "add-on",
  shop_item: "shop item",
};

/** Whether a venue FAQ has a question and an answer in `lang`. */
const writtenIn = (faq: VenueFaq | undefined, lang: string) =>
  Boolean(faq?.questions[lang]?.trim() && faq?.answers[lang]?.trim());

export function ItemFaqsTab({
  kind,
  itemId,
  itemName,
  venueId,
}: ItemFaqsTabProps) {
  const [venueFaqs, setVenueFaqs] = useState<VenueFaq[]>([]);
  const [links, setLinks] = useState<ItemFaqLink[]>([]);
  const [languages, setLanguages] = useState<string[]>(["en"]);
  const [activeLanguage, setActiveLanguage] = useState("en");
  const [loading, setLoading] = useState(true);
  const [toRemove, setToRemove] = useState<number | null>(null);
  const [removing, setRemoving] = useState(false);

  const loadLinks = useCallback(async () => {
    const result = await listItemFaqs(kind, itemId);
    if (result.success) setLinks(result.data ?? []);
    else toast.error(result.error);
  }, [kind, itemId]);

  useEffect(() => {
    const loadVenueFaqs = fetch(`/api/venues/${venueId}/faqs`)
      .then((response) => response.json())
      .then((data) => {
        if (data.success) setVenueFaqs(data.data ?? []);
        else toast.error("Failed to load venue FAQs");
      })
      .catch(() => toast.error("Failed to load venue FAQs"));
    // The venue's languages, as on the venue FAQs page.
    fetch(`/api/venues/${venueId}/details`)
      .then((response) => response.json())
      .then((data) => {
        if (data.success && data.data?.languages?.length) {
          setLanguages(data.data.languages);
          setActiveLanguage(data.data.languages[0]);
        }
      })
      .catch(() => {});
    Promise.all([loadVenueFaqs, loadLinks()]).finally(() => setLoading(false));
  }, [loadLinks, venueId]);

  const venueFaqOf = (faqId: number) =>
    venueFaqs.find((faq) => faq.faq_id === faqId);

  /** The picked FAQs in this language tab, keeping those of other tabs. */
  const pick = async (faqIdsInLanguage: number[]) => {
    const otherLanguages = links
      .map((link) => link.faq_id)
      .filter((faqId) => !writtenIn(venueFaqOf(faqId), activeLanguage));
    const result = await setItemFaqs(kind, itemId, [
      ...otherLanguages,
      ...faqIdsInLanguage,
    ]);
    if (!result.success) {
      toast.error(result.error);
      return;
    }
    await loadLinks();
    toast.success("FAQ selection updated");
  };

  const changeVisibility = async (faqId: number, visibility: FaqVisibility) => {
    // Only the language tab shown: each language has its own visibility.
    const lang = activeLanguage;
    const result = await setItemFaqVisibility(
      kind,
      itemId,
      faqId,
      lang,
      visibility,
    );
    if (!result.success) {
      toast.error(result.error);
      return;
    }
    setLinks((prev) =>
      prev.map((link) =>
        link.faq_id === faqId
          ? {
              ...link,
              visibilities: { ...link.visibilities, [lang]: visibility },
            }
          : link,
      ),
    );
    toast.success("Visibility updated");
  };

  const confirmRemove = async () => {
    if (toRemove === null) return;
    setRemoving(true);
    const result = await removeItemFaq(kind, itemId, toRemove);
    setRemoving(false);
    if (!result.success) {
      toast.error(result.error);
      return;
    }
    setLinks((prev) => prev.filter((link) => link.faq_id !== toRemove));
    setToRemove(null);
    toast.success(`FAQ removed from ${NOUN[kind]}`);
  };

  // As on a show's tab: only FAQs written in the language shown, no fallback.
  const options = venueFaqs
    .filter(
      (faq) => faq.status?.name === "active" && writtenIn(faq, activeLanguage),
    )
    .map((faq) => ({
      value: String(faq.faq_id),
      label: faq.questions[activeLanguage],
    }));
  const linksInLanguage = links.filter((link) =>
    writtenIn(venueFaqOf(link.faq_id), activeLanguage),
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Laid out as a show's FAQs tab (components/show-details/ShowFAQsTab.tsx).
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold">{TITLE[kind]}</h3>
        <p className="text-sm text-muted-foreground">
          Select and configure FAQs from your venue&apos;s FAQ bank for{" "}
          {itemName}
        </p>
      </div>

      {/* Language Tabs */}
      <div className="border-b">
        <nav className="-mb-px flex space-x-8 flex-wrap">
          {languages.map((langCode, index) => {
            const language = AVAILABLE_LANGUAGES.find(
              (l) => l.code === langCode,
            );
            return (
              <div key={langCode} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveLanguage(langCode)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeLanguage === langCode
                      ? "border-purple-600 text-purple-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {language?.flag} {language?.name || langCode.toUpperCase()}
                    {index === 0 && (
                      <Badge variant="secondary" className="text-xs ml-1">
                        Primary
                      </Badge>
                    )}
                  </span>
                </button>
              </div>
            );
          })}
        </nav>
      </div>

      <div className="space-y-6 mt-6">
        {/* FAQ Selection */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-medium">Select FAQs</h4>
                <p className="text-sm text-muted-foreground">
                  Choose which FAQs from your venue bank to include in this{" "}
                  {NOUN[kind]}
                </p>
              </div>
              <Badge variant="secondary">
                {linksInLanguage.length} selected in{" "}
                {activeLanguage.toUpperCase()}
              </Badge>
            </div>

            <MultiSelectEnhanced
              options={options}
              selected={linksInLanguage.map((link) => String(link.faq_id))}
              onChange={(values) => pick(values.map(Number))}
              placeholder="Select FAQs..."
              className="w-full"
            />
          </div>
        </Card>

        {/* FAQ Configuration */}
        <Card className="p-6">
          <div className="space-y-4">
            <div>
              <h4 className="font-medium">FAQ configuration</h4>
              <p className="text-sm text-muted-foreground">
                Configure visibility for each selected FAQ in this language.
                &quot;AI only&quot; FAQs are used by the inbox AI but never
                shown to customers.
              </p>
            </div>

            {linksInLanguage.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">
                  No FAQs selected for this {NOUN[kind]} in{" "}
                  {activeLanguage.toUpperCase()}
                </p>
                <p className="text-sm text-muted-foreground">
                  Select FAQs from the venue bank above to configure them for
                  this {NOUN[kind]}
                </p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/2">
                        Question ({activeLanguage.toUpperCase()})
                      </TableHead>
                      <TableHead className="w-1/3">Visibility</TableHead>
                      <TableHead className="w-20 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linksInLanguage.map((link) => {
                      const question =
                        venueFaqOf(link.faq_id)?.questions[activeLanguage] ??
                        "Untitled FAQ";
                      return (
                        <TableRow key={link.faq_id}>
                          <TableCell className="font-medium">
                            <div className="pr-4 break-words">
                              {question.length > 80
                                ? question.substring(0, 80) + "..."
                                : question}
                            </div>
                          </TableCell>
                          <TableCell>
                            <FaqVisibilitySelect
                              value={
                                link.visibilities[activeLanguage] ?? "visible"
                              }
                              onChange={(value) =>
                                changeVisibility(link.faq_id, value)
                              }
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              aria-label="Remove FAQ"
                              onClick={() => setToRemove(link.faq_id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </Card>
      </div>

      <ConfirmDialog
        isOpen={toRemove !== null}
        onClose={() => setToRemove(null)}
        onConfirm={confirmRemove}
        title="Remove FAQ"
        description={`Remove this FAQ from the ${NOUN[kind]}? It stays in your venue's FAQ bank.`}
        confirmText="Remove"
        confirmLoadingText="Removing..."
        isLoading={removing}
      />
    </div>
  );
}
